import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Star, Instagram, ArrowRight, ShieldCheck, VolumeX, BookOpen, Timer, ChevronDown } from 'lucide-react';
import { PRODUCTS } from '../data/products';
import { useCartStore } from '../store/useCartStore';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.7,
  delay,
  ease: [0.23, 1, 0.32, 1]
}} className={className} data-id="element-104" data-mp="261zha17p6arj">
    {children}
  </motion.div>;
export function LandingPage() {
  const navigate = useNavigate();
  const {
    addItem
  } = useCartStore();
  const scrollToProducts = () => {
    const element = document.getElementById('products');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleBuyClick = (e: React.MouseEvent, product: (typeof PRODUCTS)[0]) => {
    e.stopPropagation();
    addItem(product);
  };
  return <div className="min-h-screen bg-brand-bg text-brand-text overflow-x-hidden selection:bg-brand-accent/20 selection:text-brand-primary pt-24" data-id="element-105" data-mp="3davab6hzs9ln">
      {/* БЛОК 1: Головний екран (Hero Section) */}
      <section className="relative pt-12 pb-24 lg:pt-24 lg:pb-40 overflow-hidden" data-id="element-106" data-mp="3n89hsglxto1t">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative" data-id="element-107" data-mp="3ra3b23tszpry">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center" data-id="element-108" data-mp="3rawa0imitazj">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            ease: [0.23, 1, 0.32, 1]
          }} className="text-center lg:text-left order-1" data-id="element-109" data-mp="2igzwwss70u">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-primary/10 bg-white/50 backdrop-blur-sm mb-8" data-id="element-110" data-mp="308astmd5ukgu">
                <Star className="w-4 h-4 text-brand-accent fill-brand-accent" data-id="element-111" data-mp="397nwtziimgu6" />
                <span className="text-sm font-medium text-brand-primary tracking-wide uppercase" data-id="element-112" data-mp="399s50tng6209">Преміальна якість</span>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium leading-[1.05] mb-8 text-brand-primary tracking-tight" data-id="element-113" data-mp="3j6n53gzdcagn">
                Це не просто <br data-id="element-114" data-mp="tyoiet48qj7" />
                <span className="text-brand-accent italic" data-id="element-115" data-mp="3svn95esq9w1p">
                  Планер.
                </span>
              </h1>

              <div className="hidden lg:block mb-12" data-id="element-116" data-mp="ap8q51flqvwg">
                <p className="text-xl text-brand-text leading-relaxed max-w-lg font-light" data-id="element-117" data-mp="anpand1rsovq">
                  Елегантний спосіб навчити дитину самостійності. Без криків, без нагадувань, з любов'ю до деталей.
                </p>
              </div>

              <div className="hidden lg:flex flex-col sm:flex-row gap-6 justify-center lg:justify-start" data-id="element-118" data-mp="uasiky1jce0o">
                <button onClick={scrollToProducts} className="px-10 py-5 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full font-medium text-lg transition-all shadow-premium hover:shadow-premium-hover flex items-center justify-center gap-3 group" data-id="element-119" data-mp="fdrpor7lyd0m">
                  Обрати дизайн
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-120" data-mp="3sthdbe5xnw1g" />
                </button>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} className="relative order-2 max-w-md mx-auto lg:max-w-full flex flex-col items-center" data-id="element-121" data-mp="1nfx6r742c8y">
              <div className="relative rounded-[2rem] overflow-hidden shadow-premium bg-brand-surface mb-10 w-full aspect-[4/3] border border-white/40" data-id="element-122" data-mp="37024stim5n5a">
                <img src="https://cdn.magicpatterns.com/uploads/cdrFBjpV29uCPC4QsJUG8h/IMG_5206.png" alt="MyPlaner - Інтерактивний планер для дітей" className="w-full h-full object-cover" data-id="element-123" data-mp="2y0orfprdcefa" />
              </div>

              <div className="lg:hidden mb-10 text-center w-full" data-id="element-124" data-mp="2o4c2t6aokqre">
                <p className="text-lg text-brand-text leading-relaxed font-light" data-id="element-125" data-mp="2j4pm2mvn8sdu">
                  Елегантний спосіб навчити дитину самостійності. Без криків, без нагадувань, з любов'ю до деталей.
                </p>
              </div>

              <div className="flex lg:hidden flex-col sm:flex-row gap-4 w-full justify-center" data-id="element-126" data-mp="ha86qipmasio">
                <button onClick={scrollToProducts} className="w-full px-8 py-5 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full font-medium text-lg transition-all shadow-premium flex items-center justify-center gap-3" data-id="element-127" data-mp="pr8uroneo86g">
                  Обрати дизайн
                  <ArrowRight className="w-5 h-5" data-id="element-128" data-mp="6upiwrl7w667" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* БЛОК 2: Болі батьків (Проблема) */}
      <section className="py-24 lg:py-32 bg-brand-surfaceAlt" data-id="element-129" data-mp="fumcn7mgrv84">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-130" data-mp="fx8hgzhyzwmn">
          <FadeIn data-id="element-131" data-mp="kurxa3ubghgp">
            <div className="text-center mb-20" data-id="element-132" data-mp="kv9zri7knrx2">
              <h2 className="text-4xl md:text-5xl font-serif font-medium text-brand-primary mb-6 tracking-tight" data-id="element-133" data-mp="1rakjd4u66566">
                Знайомі ситуації?
              </h2>
              <p className="text-xl text-brand-muted max-w-2xl mx-auto font-light" data-id="element-134" data-mp="1wayr0lruzd3o">
                Кожен день схожий на день бабака, а прості справи перетворюються
                на випробування.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-10" data-id="element-135" data-mp="1gi4nhytqupty">
            {[{
            icon: <VolumeX className="w-6 h-6 text-brand-accent" data-id="element-136" data-mp="1bl4lq03xg98n" />,
            title: 'Нескінченні нагадування',
            desc: 'Втомилися щодня повторювати «почисть зуби», «прибери іграшки» або «заправ ліжко»?'
          }, {
            icon: <BookOpen className="w-6 h-6 text-brand-accent" data-id="element-137" data-mp="xk1vrc9taesg" />,
            title: 'Сварки через уроки',
            desc: 'Дитина сідає за домашнє завдання тільки після довгих вмовлянь та зіпсованого настрою?'
          }, {
            icon: <Timer className="w-6 h-6 text-brand-accent" data-id="element-138" data-mp="526d9rmtxxr5" />,
            title: 'Стресові ранки',
            desc: 'Ранкові збори в садок або школу перетворюються на хаос і забирають енергію на весь день?'
          }].map((item, idx) => <FadeIn key={idx} delay={idx * 0.15} data-id="element-139" data-mp="2gk1g11xorthg" data-mpi={idx}>
                <div className="bg-brand-bg rounded-[2rem] p-10 shadow-subtle border border-white/50 h-full flex flex-col hover:shadow-premium transition-shadow duration-500" data-id="element-140" data-mp="2gkt47royeoxp">
                  <div className="bg-brand-surface w-14 h-14 rounded-2xl flex items-center justify-center mb-8" data-id="element-141" data-mp="26tg82nw4hclv">
                    {item.icon}
                  </div>
                  <h3 className="text-2xl font-serif font-medium mb-4 text-brand-primary" data-id="element-142" data-mp="2zgsxr6xky0je">
                    {item.title}
                  </h3>
                  <p className="text-brand-text text-lg leading-relaxed font-light" data-id="element-143" data-mp="2lkfdjq1nvl18">
                    {item.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* БЛОК 3: Об'єднаний блок (Фото + Як працює + Купити) */}
      <section className="py-24 lg:py-40 bg-brand-bg" data-id="element-144" data-mp="25s2lt0nhj375">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-145" data-mp="25svjlc4guid5">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center" data-id="element-146" data-mp="20shkwkor8krs">
            {/* Ліва частина: Фото планера */}
            <div className="relative rounded-[2rem] overflow-hidden shadow-premium bg-brand-surface aspect-[4/5] lg:aspect-auto lg:h-[700px] order-2 lg:order-1 border border-white/40" data-id="element-147" data-mp="1ws75khf2oi09">
              <img src="https://cdn.magicpatterns.com/uploads/w3mah8GUReZcNXMHpqYgWg/IMG_5249.jpg" alt="Дівчинка заповнює планер" className="absolute inset-0 w-full h-full object-cover object-center" data-id="element-148" data-mp="2oittzyvqknjy" />
            </div>

            {/* Права частина: Опис */}
            <div className="flex flex-col order-1 lg:order-2" data-id="element-149" data-mp="2aqnnl4idxuxp">
              <FadeIn data-id="element-150" data-mp="3872wp2uwobxp">
                <h2 className="text-4xl md:text-5xl font-serif font-medium text-brand-primary mb-10 tracking-tight" data-id="element-151" data-mp="387cmapc1gh2v">
                  Як працює MyPlaner?
                </h2>

                <div className="space-y-10 mb-14" data-id="element-152" data-mp="3arisl3o4kjml">
                  {[{
                  title: 'Формує корисні звички',
                  desc: 'Вмивання, прибирання, читання стають автоматичними діями.'
                }, {
                  title: 'Мотивує через гру та нагороди',
                  desc: 'Збери 7 зірочок за тиждень — отримай заслужений приз!'
                }, {
                  title: 'Зменшує стрес і нагадування',
                  desc: 'Більше жодних нагадувань. Дитина сама бачить свій план.'
                }, {
                  title: 'Зона Workout',
                  desc: 'Фізична активність щодня: присідання, віджимання, планка.'
                }].map((item, idx) => <div key={idx} className="flex gap-6" data-id="element-153" data-mp="g9429vrvfjyk" data-mpi={idx}>
                      <div className="flex-shrink-0 mt-1" data-id="element-154" data-mp="latc583cxrl2">
                        <div className="w-8 h-8 rounded-full bg-brand-accent/10 flex items-center justify-center" data-id="element-155" data-mp="1g7gjojvg55wm">
                          <CheckCircle2 className="w-5 h-5 text-brand-accent" data-id="element-156" data-mp="1l5i7tj7qbvey" />
                        </div>
                      </div>
                      <div data-id="element-157" data-mp="1q435pbzie308">
                        <h4 className="text-xl font-serif font-medium text-brand-primary mb-2" data-id="element-158" data-mp="1uw4dolpn97kg">
                          {item.title}
                        </h4>
                        <p className="text-brand-text text-lg leading-relaxed font-light" data-id="element-159" data-mp="1yx6jjle2ghve">
                          {item.desc}
                        </p>
                      </div>
                    </div>)}
                </div>

                <div className="bg-brand-surfaceAlt rounded-[2rem] p-8 lg:p-10 flex flex-col sm:flex-row items-center justify-between gap-8 border border-white/50 shadow-subtle" data-id="element-160" data-mp="2dvidhka21civ">
                  <div className="text-center sm:text-left" data-id="element-161" data-mp="jgxwbhkxzz48">
                    <p className="text-sm text-brand-muted font-medium uppercase tracking-widest mb-2" data-id="element-162" data-mp="jhhe8yaleni9">
                      Інвестиція в спокій
                    </p>
                    <div className="text-4xl font-serif font-medium text-brand-primary" data-id="element-163" data-mp="2s08v1e4amxvq">
                      1000 <span className="text-xl font-sans font-light text-brand-muted" data-id="element-164" data-mp="2ny462sosipff">грн</span>
                    </div>
                  </div>
                  <button onClick={scrollToProducts} className="w-full sm:w-auto px-10 py-4 bg-brand-accent hover:bg-brand-accentHover text-white rounded-full font-medium transition-all shadow-button flex items-center justify-center gap-3 group" data-id="element-165" data-mp="2j1nhjxdoi91t">
                    Замовити зараз
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-166" data-mp="31qk9vvpimrhe" />
                  </button>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* БЛОК ТОВАРІВ (Магазин) */}
      <section id="products" className="py-24 lg:py-32 bg-brand-surface" data-id="element-167" data-mp="1qd8259qmxopy">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-168" data-mp="1rsu04vwwt65s">
          <FadeIn data-id="element-169" data-mp="1wrx8oqc3luii">
            <div className="text-center mb-20" data-id="element-170" data-mp="1wqcex2xlyflp">
              <h2 className="text-4xl md:text-5xl font-serif font-medium text-brand-primary mb-6 tracking-tight" data-id="element-171" data-mp="1wp18evcs2tqw">
                Оберіть свій дизайн
              </h2>
              <p className="text-xl text-brand-muted max-w-2xl mx-auto font-light" data-id="element-172" data-mp="1np5uwjvr2mnp">
                Створені з увагою до деталей, щоб ідеально доповнити інтер'єр дитячої кімнати.
              </p>
            </div>
          </FadeIn>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-173" data-mp="1dudt13v4xdn4">
            {PRODUCTS.map((product, idx) => <FadeIn key={product.id} delay={idx * 0.15} data-id="element-174" data-mp="142ghfw07sjnv" data-mpi={idx}>
                <div onClick={() => navigate(`/product/${product.id}`)} className="bg-brand-bg rounded-[2rem] overflow-hidden border border-white/50 hover:shadow-premium transition-all duration-500 group flex flex-col h-full cursor-pointer" data-id="element-175" data-mp="142zt4uz9ubtg">
                  <div className="relative aspect-[3/4] overflow-hidden bg-brand-surfaceAlt" data-id="element-176" data-mp="bgi4h2955qh7">
                    <img src={product.img} alt={product.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" data-id="element-177" data-mp="6iztl6bkd16k" />
                  </div>
                  <div className="p-8 flex flex-col flex-grow" data-id="element-178" data-mp="udt44nyoqlx6">
                    <h3 className="text-2xl font-serif font-medium text-brand-primary mb-3" data-id="element-179" data-mp="pgaq4j5t9gib">
                      {product.title}
                    </h3>
                    <p className="text-brand-text text-base mb-8 flex-grow line-clamp-2 font-light" data-id="element-180" data-mp="1445lk7b7dyz3">
                      {product.desc}
                    </p>

                    <div className="flex items-center justify-between mt-auto pt-6 border-t border-brand-primary/5" data-id="element-181" data-mp="1d4u3hd3pwjh9">
                      <div className="text-2xl font-serif font-medium text-brand-primary" data-id="element-182" data-mp="195bdw40ybk83">
                        {product.price} <span className="text-sm font-sans font-light text-brand-muted" data-id="element-183" data-mp="32m7okh9wbzmp">грн</span>
                      </div>
                      <button onClick={e => handleBuyClick(e, product)} className="bg-brand-primary hover:bg-brand-primaryHover text-white px-6 py-2.5 rounded-full text-sm font-medium transition-colors" data-id="element-184" data-mp="37jhbnq9tbz7d">
                        Купити
                      </button>
                    </div>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* БЛОК FAQ: Питання та відповіді */}
      <section className="py-24 lg:py-32 bg-brand-bg" data-id="element-185" data-mp="2xo5w55qkynyk">
        <div className="max-w-3xl mx-auto px-6 lg:px-8" data-id="element-186" data-mp="2xljnf2rye2er">
          <FadeIn data-id="element-187" data-mp="8us9c8xc24k3">
            <div className="text-center mb-20" data-id="element-188" data-mp="8vaddrq8ln2k">
              <h2 className="text-4xl md:text-5xl font-serif font-medium text-brand-primary mb-6 tracking-tight" data-id="element-189" data-mp="cx5d4rsirse8">
                Часті запитання
              </h2>
            </div>
          </FadeIn>

          <div className="space-y-6" data-id="element-190" data-mp="1te4h78sroflz">
            {[{
            q: 'Для якого віку підходить планер?',
            a: 'Планер ідеально підходить для дітей від 4 до 10 років. Малюки можуть орієнтуватися по картинках, а старші діти — читати завдання самостійно.'
          }, {
            q: 'Як він кріпиться?',
            a: 'Планер має повністю магнітну основу. Він надійно тримається на холодильнику або будь-якій іншій металевій поверхні і не сповзає.'
          }, {
            q: 'Чим писати на планері?',
            a: 'У комплекті йде спеціальний маркер з губкою. Дитина може легко ставити галочки та стирати їх наприкінці тижня. Поверхня планера багаторазова.'
          }].map((faq, idx) => {
            const [isOpen, setIsOpen] = useState(false);
            return <FadeIn key={idx} delay={idx * 0.1} data-id="element-191" data-mp="3e7uxqonsgl59" data-mpi={idx}>
                  <div className="border border-brand-primary/10 rounded-2xl overflow-hidden bg-brand-surfaceAlt/50" data-id="element-192" data-mp="3e6a0use04q48">
                    <button onClick={() => setIsOpen(!isOpen)} className="w-full px-8 py-6 flex items-center justify-between text-left focus:outline-none hover:bg-brand-surfaceAlt transition-colors" data-id="element-193" data-mp="3j0o0j6zp5jep">
                      <span className="font-serif font-medium text-brand-primary text-xl" data-id="element-194" data-mp="1rhjp54z241j">
                        {faq.q}
                      </span>
                      <ChevronDown className={`w-5 h-5 text-brand-accent transition-transform duration-500 ease-[0.23,1,0.32,1] ${isOpen ? 'rotate-180' : ''}`} data-id="element-195" data-mp="3nxmt3l3ppias" />
                    </button>
                    <AnimatePresence data-id="element-196" data-mp="2ci7ukcvuuidk">
                      {isOpen && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.4,
                    ease: [0.23, 1, 0.32, 1]
                  }} data-id="element-197" data-mp="2hhaszehbxqck">
                          <div className="px-8 pb-8 text-brand-text text-lg leading-relaxed font-light" data-id="element-198" data-mp="1m6dxrhqf56s3">
                            {faq.a}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </FadeIn>;
          })}
          </div>
        </div>
      </section>

      {/* БЛОК 5: Соціальний доказ та довіра */}
      <section className="py-24 lg:py-32 bg-brand-surface border-t border-brand-primary/5" data-id="element-199" data-mp="hyxsnjnrh8ni">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-200" data-mp="lzzr3y9utgof">
          <FadeIn data-id="element-201" data-mp="m0jbdlmz8h5m">
            <div className="flex justify-center mb-12" data-id="element-202" data-mp="m22r9b4q0q6v">
              <div className="inline-flex items-center gap-3 bg-brand-bg px-6 py-3 rounded-full border border-brand-primary/10 shadow-subtle" data-id="element-203" data-mp="qyksi10ue6eg">
                <ShieldCheck className="w-5 h-5 text-brand-accent" data-id="element-204" data-mp="vw2uf7gfzlcy" />
                <span className="font-medium text-brand-primary text-sm tracking-wide uppercase" data-id="element-205" data-mp="10x0iu1s1pbmw">
                  Зроблено в Україні
                </span>
              </div>
            </div>

            <div className="text-center mb-20" data-id="element-206" data-mp="19qdn1zvk7zoa">
              <h2 className="text-4xl md:text-5xl font-serif font-medium text-brand-primary mb-6 tracking-tight" data-id="element-207" data-mp="1eonj0cp3z4ay">
                Батьки рекомендують
              </h2>
              <p className="text-xl text-brand-muted font-light" data-id="element-208" data-mp="1jlx4jh9dmvti">
                Сотні сімей вже забули про крики вранці.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-10" data-id="element-209" data-mp="396qa63s00d7r">
            {[{
            name: 'Анна, мама 6-річного Максима',
            text: 'Це просто порятунок! Раніше кожен ранок починався зі сліз та вмовлянь почистити зуби. Тепер син сам біжить до холодильника відмічати галочки. Чекає кінця тижня, щоб отримати свій приз.',
            img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
          }, {
            name: 'Олена, мама двійнят',
            text: "Купили відразу два планери. У дітей з'явився змагальний дух! Самі заправляють ліжко, роблять зарядку. Я нарешті можу спокійно випити каву вранці.",
            img: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
          }, {
            name: 'Ігор, тато 8-річної Софії',
            text: "Відмінна річ для привчання до дисципліни без тиску. Донька сама обирає собі 'Завдання тижня' і дуже пишається, коли збирає всі зірочки. Якість магніту супер!",
            img: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
          }].map((review, idx) => <FadeIn key={idx} delay={idx * 0.15} data-id="element-210" data-mp="1mcxjwr2kwd12" data-mpi={idx}>
                <div className="bg-brand-bg p-10 rounded-[2rem] h-full flex flex-col border border-white/50 shadow-subtle" data-id="element-211" data-mp="2j1lpgnx3y7tl">
                  <div className="flex gap-1.5 mb-8" data-id="element-212" data-mp="2f11rji4pn06e">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-brand-accent fill-brand-accent" data-id="element-213" data-mp="2a7h4gl4nnqlz" data-mpi={i} />)}
                  </div>
                  <p className="text-brand-text text-lg leading-relaxed mb-10 flex-grow font-light italic" data-id="element-214" data-mp="32qnri7swpjfg">
                    "{review.text}"
                  </p>
                  <div className="flex items-center gap-5" data-id="element-215" data-mp="35astnfy1tugt">
                    <img src={review.img} alt={review.name} className="w-12 h-12 rounded-full object-cover bg-brand-surfaceAlt border border-white" data-id="element-216" data-mp="3a8ubxynnhpr8" />
                    <p className="font-serif font-medium text-brand-primary text-lg" data-id="element-217" data-mp="3jaleiumw9ggv">
                      {review.name}
                    </p>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* БЛОК 6: Підвал (Footer) */}
      <footer className="bg-brand-primary text-brand-bg pt-24 pb-12" data-id="element-218" data-mp="1udaraw1gfto">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center" data-id="element-219" data-mp="6sn7hpxevohe">
          <FadeIn data-id="element-220" data-mp="6ty8xj3dwk5c">
            <h2 className="text-4xl md:text-5xl font-serif font-medium mb-8 tracking-tight" data-id="element-221" data-mp="au0fa5k2e1qy">
              Готові до спокійних ранків?
            </h2>
            <p className="text-xl text-brand-bg/70 mb-12 max-w-xl mx-auto font-light" data-id="element-222" data-mp="ftd2wsirfcul">
              Замовте MyPlaner сьогодні та почніть формувати корисні звички
              вашої дитини вже завтра.
            </p>

            <button className="px-10 py-5 bg-brand-accent hover:bg-brand-accentHover text-white rounded-full font-medium text-lg transition-colors mb-20 inline-flex items-center gap-3 shadow-button" data-id="element-223" data-mp="27sa1pzwajt14">
              Замовити за 1000 грн
              <ArrowRight className="w-5 h-5" data-id="element-224" data-mp="2vjfo6xwqljke" />
            </button>
          </FadeIn>

          <div className="border-t border-white/10 pt-12 flex flex-col md:flex-row justify-between items-center gap-8" data-id="element-225" data-mp="2ql4f4pe9nd38">
            <div className="text-2xl font-serif font-medium tracking-tight" data-id="element-226" data-mp="34kvr6kl82jel">
              MyPlaner<span className="text-brand-accent" data-id="element-227" data-mp="2zl0z4dppnnry">.</span>
            </div>

            <div className="flex items-center gap-6" data-id="element-228" data-mp="2zjprg2t59vvk">
              <a href="https://www.instagram.com/myplaner.ua/p/DXq4XE3jJA8/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-brand-bg/60 hover:text-brand-bg transition-colors text-sm font-medium tracking-wide" data-id="element-229" data-mp="1a7e04nllz4et">
                <Instagram className="w-5 h-5" data-id="element-230" data-mp="3lw2bdjgtr81u" />
                <span data-id="element-231" data-mp="383colcth1z3o">@myplaner.ua</span>
              </a>
            </div>
          </div>

          <div className="mt-12 text-brand-bg/40 text-sm flex flex-col md:flex-row justify-center gap-8 font-light" data-id="element-232" data-mp="3c3msmjxpeffw">
            <a href="#" className="hover:text-brand-bg transition-colors" data-id="element-233" data-mp="2y3c0zeokkwto">
              Умови доставки та оплати
            </a>
            <a href="#" className="hover:text-brand-bg transition-colors" data-id="element-234" data-mp="334rn7a2hh9dn">
              Політика конфіденційності
            </a>
            <span data-id="element-235" data-mp="2t5akn1ij3blw">
              © {new Date().getFullYear()} MyPlaner. Всі права захищені.
            </span>
          </div>
        </div>
      </footer>
    </div>;
}